/**
 * A finished test call as a ticket (ADR-309), apart from React.
 *
 * The capture (`GET /api/workflow-defns/:id/tests/:interactionId/capture`)
 * holds everything the call left behind. This turns it into what the tester
 * hands over: Markdown for the clipboard, written to be pasted straight into a
 * ticket or to Claude, and JSON for the download. The Markdown puts the
 * tester's note first, then one timeline merging the transcript, the worker's
 * trace and the call's events by time, then the report and the definition
 * that ran, so whoever fixes it can read the call in order.
 */

import type { WorkflowTestCapture, WorkflowTestTraceEntry } from '@vagusx/clients';

type Message = WorkflowTestCapture['transcript'][number];
type Event = WorkflowTestCapture['events'][number];

interface Row {
  at: number;
  /** Ties keep source order: transcript, then trace, then events. */
  order: number;
  line: string;
}

function compact(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** A field of a JSON object payload, or undefined. */
function field(payload: unknown, key: string): unknown {
  return payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>)[key]
    : undefined;
}

function messageLine(m: Message): string {
  switch (m.kind) {
    case 'user_message':
      return `CALLER  ${compact(m.text)}`;
    case 'agent_message':
      return `AGENT${m.agentId ? ` [${m.agentId}]` : ''}  ${compact(m.text)}`;
    case 'tool_request':
      return `TOOL →  ${m.toolName ?? '?'}(${compact(field(m.payload, 'arguments'))})`;
    case 'tool_response': {
      const timing = m.toolDurationMs === null ? '' : ` ${m.toolDurationMs}ms`;
      const body = field(m.payload, 'error') ?? field(m.payload, 'result');
      return `TOOL ←  ${m.toolName ?? '?'} ${m.toolStatus ?? ''}${timing}: ${compact(body)}`;
    }
    default:
      return `${m.kind.toUpperCase()}  ${compact(m.text ?? m.payload)}`;
  }
}

function traceLine(e: WorkflowTestTraceEntry): string {
  switch (e.kind) {
    case 'log':
      return `LOG ${e.level === 'info' ? '' : `${e.level.toUpperCase()} `} ${e.text}`;
    case 'walk':
      return `WALK  ${e.status} → ${e.node ?? '(no step)'}`;
    case 'node':
      return `STEP  ${e.node}: ${e.status}${e.outcome ? ` (${e.outcome})` : ''}${e.revisits ? ` revisit ${e.revisits}` : ''} data=${e.data}`;
    case 'wrote':
      return `WROTE ${e.node}: ${e.facts}`;
    case 'state':
      return `·     ${e.who} ${e.state}`;
    case 'error':
      return `ERROR ${e.source}: ${e.message}`;
  }
}

function eventLine(e: Event): string {
  const failed = e.error ? ` FAILED: ${e.error}` : '';
  const data = e.data === null ? '' : ` ${compact(e.data)}`;
  return `EVENT ${e.type}: ${e.title}${data}${failed}`;
}

/** Seconds since `start`, as `+12.3s`. */
function offset(at: number, start: number): string {
  const s = (at - start) / 1000;
  return `${s >= 0 ? '+' : '-'}${Math.abs(s).toFixed(1)}s`.padStart(8);
}

/** Every row of the call, in time order. Exported for the tests. */
export function timeline(capture: WorkflowTestCapture): string[] {
  const rows: Row[] = [
    ...capture.transcript.map((m) => ({ at: Date.parse(m.at), order: 0, line: messageLine(m) })),
    ...(capture.trace?.entries ?? []).map((e) => ({
      at: Date.parse(e.at),
      order: 1,
      line: traceLine(e),
    })),
    ...capture.events.map((e) => ({ at: Date.parse(e.at), order: 2, line: eventLine(e) })),
  ];
  const start = Date.parse(
    capture.trace?.startedAt ?? capture.test.startedAt ?? capture.test.createdAt
  );
  return rows
    .map((r, i) => ({ ...r, i }))
    .sort((a, b) => a.at - b.at || a.order - b.order || a.i - b.i)
    .map((r) => `${offset(r.at, start)}  ${r.line}`);
}

/** What is missing, said plainly: a missing part is often the finding. */
function gaps(capture: WorkflowTestCapture): string[] {
  const out: string[] = [];
  if (capture.test.status === 'pending' && capture.transcript.length === 0) {
    out.push('The call never started: no worker picked it up (status is still pending).');
  }
  if (!capture.trace)
    out.push('No worker trace arrived (the job may have crashed, or it is still winding down).');
  if (!capture.report) out.push('No report arrived: the walk never closed, or posting it failed.');
  if (capture.trace && capture.trace.dropped > 0) {
    out.push(`The trace hit its bound: ${capture.trace.dropped} later entries were dropped.`);
  }
  return out;
}

/**
 * A code block that its content cannot close: the fence is one backtick longer
 * than the longest run of backticks inside (CommonMark), and never under three.
 */
export function codeBlock(lang: string, body: string): string {
  const longest = Math.max(0, ...(body.match(/`+/g) ?? []).map((run) => run.length));
  const fenceMark = '`'.repeat(Math.max(3, longest + 1));
  return [`${fenceMark}${lang}`, body, fenceMark].join('\n');
}

function fence(value: unknown): string {
  return codeBlock('json', JSON.stringify(value, null, 2));
}

/** The ticket, as Markdown, for the clipboard. */
export function captureMarkdown(capture: WorkflowTestCapture, note: string): string {
  const t = capture.test;
  const what = t.nodeId ? `step \`${t.nodeId}\`` : 'the whole workflow';
  const missing = gaps(capture);
  return [
    `# Workflow test: ${t.workflowName}, ${what}`,
    '',
    '## What went wrong',
    '',
    note.trim() || '_The tester left no note._',
    '',
    '## The test',
    '',
    `- Workflow: ${t.workflowName} (\`${t.workflowId}\`), draft revision ${t.fromDraftRevision}, snapshot \`${t.snapshotId}\``,
    `- Tested: ${what}`,
    `- Test call: interaction \`${t.interactionId}\`, status ${t.status}; created ${t.createdAt}, started ${t.startedAt ?? '—'}, ended ${t.endedAt ?? '—'}`,
    `- Worker: ${capture.trace?.worker ?? 'unknown (no trace)'}`,
    `- Tester's picks: ${capture.trace?.inputs ? `\`${compact(capture.trace.inputs)}\`` : 'none'}`,
    ...(capture.testPlan ? [`- Test plan: \`${compact(capture.testPlan)}\``] : []),
    ...(missing.length > 0 ? ['', '## Missing', '', ...missing.map((m) => `- ${m}`)] : []),
    '',
    '## Timeline',
    '',
    'Transcript (CALLER, AGENT, TOOL), the worker trace (LOG, WALK, STEP, WROTE, ERROR, and `·` for who is speaking) and call events (EVENT), by time since the call started.',
    '',
    codeBlock('text', timeline(capture).join('\n')),
    '',
    '## What the test understood',
    '',
    capture.report ? fence(capture.report) : '_No report._',
    '',
    '## The definition that ran',
    '',
    fence(capture.definition),
    '',
  ].join('\n');
}

/** The ticket, as JSON, for the download. */
export function captureJson(capture: WorkflowTestCapture, note: string): string {
  return JSON.stringify({ note: note.trim() || null, ...capture }, null, 2);
}

export function captureFilename(capture: WorkflowTestCapture): string {
  return `workflow-test-${capture.test.interactionId.slice(0, 8)}.json`;
}
